import React, { useState } from 'react';
import './ReorderRecommendations.css';

function ReorderRecommendations({ stockData, supplierConfig, fbaTransferDelay, supplierTransitDelay }) {
  const safetyStock = 8; // Stock de sécurité : 8 jours
  const [includeHomeStock, setIncludeHomeStock] = useState(true); // Option pour inclure le stock à la maison
  const [includeTransitStock, setIncludeTransitStock] = useState(true); // Option pour inclure le stock en transit
  const [showOnlyNeeded, setShowOnlyNeeded] = useState(true); // Par défaut, n'afficher que les produits à commander
  
  if (!stockData || stockData.length === 0) {
    return <p>Aucune donnée disponible pour les recommandations</p>;
  }

  // Fonction pour obtenir le délai du fournisseur
  const getSupplierLeadTime = (supplierId) => {
    if (!supplierConfig || !supplierConfig.suppliers) return null;
    
    const supplier = supplierConfig.suppliers.find(s => s.id === parseInt(supplierId));
    return supplier ? supplier.leadTime : null;
  };

  // Analyser tous les produits et créer les recommandations
  const analyzeProducts = () => {
    return stockData
      .filter(item => {
        // FILTRE: Ne traiter que les produits avec stock total > 0 (produits actifs)
        const totalStock = (item.fbmQuantity || 0) + 
                          (item.fbaQuantity || 0) + 
                          (item.fcTransfer || 0) + 
                          (item.homeQuantity || 0) + 
                          (item.transitQuantity || 0);
        return totalStock > 0; // Exclure les SKUs abandonnés (stock = 0)
      })
      .map(item => {
        const sku = item.sku;
        const currentStock = parseInt(item.quantity) || 0;
        const homeStock = includeHomeStock ? (parseInt(item.homeQuantity) || 0) : 0;
        const transitStock = includeTransitStock ? (parseInt(item.transitQuantity) || 0) : 0;
        const fbaStock = parseInt(item.fbaQuantity) || 0;
        const fcTransferStock = parseInt(item.fcTransfer) || 0;
        const fbmStock = parseInt(item.fbmQuantity) || 0;
        
        // Utiliser les ventes quotidiennes moyennes du fichier Excel importé
        // Utiliser 0.1 comme valeur par défaut uniquement si les ventes ne sont pas explicitement 0
        const originalSales = parseFloat(item.dailySales);
        const estimatedDailySales = originalSales === 0 ? 0 : (originalSales || 0.1);        
        
        // Trouver le fournisseur et le délai de livraison pour ce produit
        let leadTime = 14; // Délai par défaut
        let supplierName = 'Non assigné';
        
        // Utiliser supplier_id directement depuis les données produit (déjà jointure SQL côté API)
        if (item.supplier_id && supplierConfig && supplierConfig.suppliers) {
          const supplier = supplierConfig.suppliers.find(s => s.id === parseInt(item.supplier_id));
          
          if (supplier) {
            leadTime = supplier.leadTime || supplier.lead_time || 14;
            supplierName = supplier.name;
          }
        } else if (supplierConfig && supplierConfig.productSuppliers && supplierConfig.productSuppliers[sku]) {
          // Fallback vers localStorage pour compatibilité durant la migration
          const supplierId = parseInt(supplierConfig.productSuppliers[sku]);
          const supplier = supplierConfig.suppliers.find(s => s.id === supplierId);
          
          if (supplier) {
            leadTime = supplier.leadTime || supplier.lead_time || 14;
            supplierName = supplier.name;
          }
        }
        
        // MODIFICATION 1: Calcul simplifié du stock effectif
        // Plus de déduction pour la consommation pendant les transferts
        const effectiveStock = currentStock + homeStock + transitStock;
        
        // Calculer le nombre de jours restants avec le stock actuel (FBA + FBM)
        const daysWithCurrentStock = estimatedDailySales > 0
          ? Math.floor((fbaStock + fbmStock) / estimatedDailySales) 
          : 999;
        
        // Calculer spécifiquement les jours de stock Amazon TOTAL (FBA + FC Transfer pour les recommandations d'envoi)
        const stockAmazonTotal = fbaStock + fcTransferStock;
        const daysWithFBAStock = estimatedDailySales > 0 && stockAmazonTotal > 0
          ? Math.floor(stockAmazonTotal / estimatedDailySales)
          : 0;
        
        // MODIFICATION 2: Calcul simplifié des jours supplémentaires
        // Plus de déduction pour la consommation pendant les transferts
        const additionalDaysFromHome = estimatedDailySales > 0 && homeStock > 0
          ? Math.floor(homeStock / estimatedDailySales)
          : 0;
        
        const additionalDaysFromTransit = estimatedDailySales > 0 && transitStock > 0
          ? Math.floor(transitStock / estimatedDailySales)
          : 0;
        
        // MODIFICATION 3: Calcul simplifié du total des jours restants
        // Simplement la somme des jours de stock dans tous les emplacements
        const totalDaysRemaining = daysWithCurrentStock + additionalDaysFromHome + additionalDaysFromTransit;
        
        // Déterminer si une commande est nécessaire, en ignorant si déjà en transit
        const needsReorder = totalDaysRemaining <= (leadTime + safetyStock) && transitStock === 0;
        
        // Calculer la quantité à commander (pour couvrir le délai + stock de sécurité + 30 jours)
        const reorderQuantity = Math.ceil(estimatedDailySales * (leadTime + safetyStock + 30));
        
        // Calcul des recommandations d'envoi à Amazon avec la nouvelle logique
        const today = new Date();
        let amazonSendDate = null;
        let amazonSendOverdue = false;
        let canSendToAmazon = false;

        // 1. Vérifier s'il faut envoyer à Amazon (stock FBA < seuil d'affichage)
        const displayThreshold = fbaTransferDelay + safetyStock + 7; // délai + sécurité + préparation (25 jours)
        const criticalLevel = fbaTransferDelay + safetyStock; // délai + sécurité (18 jours)
        const needToSendToAmazon = stockAmazonTotal > 0 && daysWithFBAStock <= displayThreshold;

        if (needToSendToAmazon) {
          canSendToAmazon = true;
          
          // Calculer les jours avant d'atteindre le niveau critique
          const daysBeforeCritical = daysWithFBAStock - criticalLevel;
          
          if (daysBeforeCritical <= 0) {
            // Rouge : deadline dépassée
            amazonSendOverdue = true;
            amazonSendDate = "Aujourd'hui";
          } else {
            // Bleu : encore du temps avant la deadline
            amazonSendOverdue = false;
            const futureDate = new Date(today);
            futureDate.setDate(today.getDate() + daysBeforeCritical);
            amazonSendDate = futureDate.toLocaleDateString();
          }
        }

        // NOUVELLE FONCTIONNALITÉ: Calcul de la quantité à envoyer à Amazon
        let amazonSendQuantity = 0;
        
        if (canSendToAmazon) {
          // Calcul similaire à celui pour les fournisseurs
          // quantitéEnvoiAmazon = ventes quotidiennes × (délai transfert FBA + stock sécurité + période additionnelle) - stock FBA actuel
          // CORRECTION: Assurer que la quantité à envoyer n'est jamais négative
          const rawAmazonSendQuantity = Math.max(0, Math.ceil(estimatedDailySales * (fbaTransferDelay + safetyStock + 30) - stockAmazonTotal));
          
          // Stock disponible pour envoi (stock FBM + stock à la maison)
          const availableForSend = fbmStock + homeStock;
          
          // La quantité à envoyer ne peut pas dépasser ce qui est disponible
          amazonSendQuantity = Math.min(rawAmazonSendQuantity, availableForSend);
          
          // Quantité minimale raisonnable (si non nul)
          if (amazonSendQuantity > 0 && amazonSendQuantity < 5) {
            amazonSendQuantity = Math.min(5, availableForSend);
          }
        }

        // Calcul de la date de commande fournisseur
        let reorderDateText;
        if (totalDaysRemaining <= leadTime) {
          // Rouge : URGENT - commander maintenant
          reorderDateText = today.toLocaleDateString();
        } else {
          // Jaune : WARNING - deadline de commande
          // Calcul : quand commander pour recevoir avant que ça devienne critique
          const daysUntilOrderDeadline = totalDaysRemaining - leadTime;
          const deadlineDate = new Date(today);
          deadlineDate.setDate(today.getDate() + daysUntilOrderDeadline);
          reorderDateText = deadlineDate.toLocaleDateString();
        }
        
        return {
          sku,
          productName: item.productName || '',
          currentStock,
          homeStock,
          transitStock,
          fbaStock,
          fcTransferStock,
          stockAmazonTotal,
          fbmStock,
          dailySales: estimatedDailySales.toFixed(2),
          daysWithCurrentStock,
          daysWithFBAStock,
          additionalDaysFromHome,
          additionalDaysFromTransit,
          daysRemaining: totalDaysRemaining,
          effectiveStock,
          needsReorder,
          reorderQuantity: needsReorder ? reorderQuantity : 0,
          expectedDepletion: new Date(Date.now() + (totalDaysRemaining * 24 * 60 * 60 * 1000)).toLocaleDateString(),
          supplierName,
          leadTime,
          amazonSendDate: amazonSendDate || '-',
          canSendToAmazon,
          amazonSendOverdue,
          amazonSendQuantity,  // Nouvelle propriété
          reorderDate: reorderDateText
        };
      })
      .sort((a, b) => a.daysRemaining - b.daysRemaining);
  };

  // Obtenir toutes les recommandations
  const allRecommendations = analyzeProducts();
  
  // Pour les recommandations de commande fournisseur, on applique le filtre si demandé
  const supplierRecommendations = showOnlyNeeded 
    ? allRecommendations.filter(item => item.needsReorder) 
    : allRecommendations;
  
  // Pour les recommandations d'envoi à Amazon, on utilise un filtre différent
  const amazonRecommendations = allRecommendations.filter(item => 
    item.canSendToAmazon && item.amazonSendQuantity > 0
  );
  return (
    <div className="reorder-recommendations">
      <h2>Recommandations de réapprovisionnement</h2>
      
      <div className="settings-panel">
        <div className="setting">
          <label>
            <input
              type="checkbox"
              checked={includeHomeStock}
              onChange={(e) => setIncludeHomeStock(e.target.checked)}
            />
            Inclure le stock FBA à la maison
          </label>
        </div>
        <div className="setting">
          <label>
            <input
              type="checkbox"
              checked={includeTransitStock}
              onChange={(e) => setIncludeTransitStock(e.target.checked)}
            />
            Inclure le stock en transit
          </label>
        </div>
        <div className="setting">
          <label>
            <input
              type="checkbox"
              checked={showOnlyNeeded}
              onChange={(e) => setShowOnlyNeeded(e.target.checked)}
            />
            Afficher uniquement les produits à commander
          </label>
        </div>
        <div className="delays-info">
          <span>Délai de transfert FBA: {fbaTransferDelay} jours</span>
          <span>Stock de sécurité: {safetyStock} jours</span>
          <span>Délai de préparation: 7 jours</span>
        </div>
      </div>
      
      <div className="recommendations-count">
        <span className="total-count">
          {showOnlyNeeded 
            ? `${supplierRecommendations.length} produit(s) à commander` 
            : `${allRecommendations.filter(item => item.needsReorder).length} produit(s) à commander sur ${allRecommendations.length} produit(s)`}
        </span>
        <span className="filtered-count">
          {!showOnlyNeeded && allRecommendations.length > allRecommendations.filter(item => item.needsReorder).length && 
            `(${allRecommendations.length - allRecommendations.filter(item => item.needsReorder).length} produit(s) avec stock suffisant)`}
        </span>
      </div>
      
      {/* Section des recommandations d'envoi à Amazon - Toujours visible si des produits nécessitent un envoi */}
      {amazonRecommendations.length > 0 ? (
        <div>
          <h3>Recommandations d'envoi à Amazon</h3>
          <div className="table-wrapper">
            <table className="recommendations-table">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Produit</th>
                  <th>Stock Amazon (FBA+FC)</th>
                  <th>Dispo (FBM+Maison)</th>
                  <th>Ventes/jour</th>
                  <th>Jours Amazon restants</th>
                  <th>Date d'envoi</th>
                  <th>Qté à envoyer</th>
                </tr>
              </thead>
              <tbody>
                {amazonRecommendations.map((item, index) => (
                  <tr key={index} className={item.needsReorder ? 'needs-reorder' : ''}>
                    <td>{item.sku}</td>
                    <td>{item.productName ? (item.productName.length > 55 ? item.productName.substring(0, 55) + '...' : item.productName) : ''}</td>
                    <td>{item.stockAmazonTotal} <span style={{color: '#666', fontSize: '0.8rem'}}>({item.fbaStock}+{item.fcTransferStock})</span></td>
                    <td>{item.fbmStock + item.homeStock}</td>
                    <td>{item.dailySales}</td>
                    <td className={
                      item.daysWithFBAStock <= fbaTransferDelay ? 'urgent' : 
                      item.daysWithFBAStock <= (fbaTransferDelay + safetyStock) ? 'warning' : ''
                    }>
                      {item.daysWithFBAStock}
                    </td>
                    <td className={item.amazonSendOverdue ? "amazon-send-overdue" : "amazon-send"}>
                      {item.amazonSendDate}
                    </td>
                    <td className="stock-value">
                      {item.amazonSendQuantity}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div>
          <h3>Recommandations d'envoi à Amazon</h3>
          <p className="no-reorder-needed">Aucun envoi à Amazon nécessaire pour le moment</p>
        </div>
      )}
      
      {/* Section des recommandations de commande fournisseur */}
      {supplierRecommendations.length > 0 ? (
        <div>
          <h3>Recommandations de commande fournisseur</h3>
          <div className="table-wrapper">
            <table className="recommendations-table">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Produit</th>
                  <th>Stock Total</th>
                  <th>Ventes/jour</th>
                  <th>Jours restants</th>
                  <th className="date-tooltip" data-tooltip="Date à laquelle vous devriez commander auprès de votre fournisseur en fonction du délai de livraison">
                    Date limie de commande
                  </th>
                  <th>Fournisseur</th>
                  <th>Délai (jours)</th>
                  <th>Quantité à commander</th>
                </tr>
              </thead>
              <tbody>
                {supplierRecommendations.map((item, index) => (
                  <tr key={index} className={item.needsReorder ? 'needs-reorder' : ''}>
                    <td>{item.sku}</td>
                    <td>{item.productName ? (item.productName.length > 55 ? item.productName.substring(0, 55) + '...' : item.productName) : ''}</td>
                    <td>{item.currentStock}</td>
                    <td>{item.dailySales}</td>
                    <td className={
                      item.needsReorder ? 
                        (item.daysRemaining <= item.leadTime ? 'urgent' : 'warning') 
                        : ''
                    }>
                      {item.daysRemaining}
                    </td>
                    <td>
                      {item.reorderDate}
                    </td>
                    <td>{item.supplierName}</td>
                    <td>{item.leadTime}</td>
                    <td>{item.reorderQuantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div>
          <h3>Recommandations de commande fournisseur</h3>
          <p className="no-reorder-needed">Aucune commande à passer pour le moment</p>
        </div>
      )}
      
      <div className="explanation">
        <h3>Comment sont calculées les recommandations</h3>
        <p>
          1. Nous utilisons les ventes quotidiennes moyennes sur 7 jours de votre fichier d'inventaire Excel
          <br />
          2. Le stock effectif est calculé comme suit:
          <br />
          &nbsp;&nbsp;&nbsp;• Stock actuel (FBA + FBM)
          <br />
          &nbsp;&nbsp;&nbsp;• + Stock à la maison
          <br />
          &nbsp;&nbsp;&nbsp;• + Stock en transit
          <br />
          3. Si les jours de stock restants sont inférieurs au délai de livraison + stock de sécurité ({safetyStock} jours), nous recommandons de commander
          <br />
          4. La quantité recommandée couvre le délai de livraison + stock de sécurité + 30 jours de ventes
          <br />
          5. Les délais de livraison sont spécifiques à chaque fournisseur configuré
          <br />
          6. Les dates prévisionnelles sont calculées en fonction de vos ventes actuelles et des délais configurés
          <br />
          7. Les recommandations d'envoi à Amazon sont indépendantes des recommandations de commande fournisseur
          <br />
          8. Les recommandations d'envoi Amazon apparaissent quand le stock FBA ≤ {fbaTransferDelay + safetyStock + 7} jours (délai + sécurité + préparation)
          <br />
          9. La date d'envoi à Amazon est calculée pour que le stock arrive avant d'atteindre le niveau critique ({fbaTransferDelay + safetyStock} jours)
          <br />
          10. La quantité à envoyer à Amazon est calculée pour couvrir le délai de transfert + stock de sécurité + 30 jours, limitée par votre stock disponible
        </p>
      </div>
    </div>
  );
}

export default ReorderRecommendations;